import { useBlockPermission, useModulePermission } from '@bechtel/client-common';
import { drawerValues as defaultValues } from '@bechtel/client-work-packages';
import { Box, Icon, IconSize, Menu, MenuOptions, Text } from '@bechtel/gds';
import { MilestoneStatusCode, WorkPackageFilterByTerm } from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { flatMap } from 'lodash';
import { FC, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ButtonStyled, MenuItemStyled, PhotoNotificationStyled } from './styled';
import { WorkListDetailButtonProps, WorkListMenu, WorkListMenuType } from './types';

function getWorkListMenuItems(
  milestoneStatusCode: MilestoneStatusCode,
  canViewWorkPackageModule: boolean
): WorkListMenu[] {
  const menuItems = [
    { label: t`View Details`, type: WorkListMenuType.COMMODITY_DETAIL },
    { label: t`Add/View Photos`, type: WorkListMenuType.ADD_VIEW_PHOTOS },
  ];
  if (canViewWorkPackageModule) {
    menuItems.push({ label: t`Go To Work Package`, type: WorkListMenuType.WORK_PACKAGE });
  }
  if (
    milestoneStatusCode === MilestoneStatusCode.UNCLAIMED ||
    milestoneStatusCode === MilestoneStatusCode.PLANNED ||
    milestoneStatusCode === MilestoneStatusCode.REJECTED
  ) {
    menuItems.push({ label: t`Create Block`, type: WorkListMenuType.CREATE_BLOCK });
  }
  return menuItems;
}

export const WorkListDetailButton: FC<WorkListDetailButtonProps> = ({
  milestone,
  onWorkListMenuSelect,
  isPendingList,
}) => {
  const { canViewWorkPackageModule } = useModulePermission();
  const navigate = useNavigate();
  //@ts-ignore ignore undefined type warning as commodity wrapper will either have a weld or material commodity always
  const commodity: MaterialCommodity | Weld = milestone.commodity.materialCommodity
    ? milestone.commodity.materialCommodity
    : milestone.commodity.weld;
  const assignedCrewMembers = [
    ...commodity.workPackage?.primaryOwners,
    ...commodity.workPackage?.secondaryOwners,
    ...commodity.workPackage?.otherOwners,
  ];

  const { canCreateBlock } = useBlockPermission(assignedCrewMembers, commodity.workPackage?.facilities);
  const workListMenuItems = getWorkListMenuItems(milestone.currentStatus.codeI18n, canViewWorkPackageModule);
  const attachments = flatMap(milestone.attachments, 'id');
  const photosCount = attachments.length;
  const { i18n } = useLingui();

  const onMenuSelect = useCallback(
    (menuType: WorkListMenuType) => {
      if (menuType === WorkListMenuType.WORK_PACKAGE) {
        const encodedId = encodeURIComponent(commodity?.workPackage.id);
        navigate(
          {
            pathname: `work-packages/${encodedId}`,
            search: `?tag=${commodity?.workPackage.tag}`,
          },
          {
            state: {
              drawerValues: {
                ...defaultValues,
                filterBy: WorkPackageFilterByTerm.ALL,
                searchScope: false,
              },
            },
          }
        );
      } else {
        onWorkListMenuSelect(menuType);
      }
    },
    [commodity, onWorkListMenuSelect]
  );

  return (
    <Box mr={{ sm: '400' }} style={{ width: isPendingList ? '100%' : '50%' }}>
      <Menu>
        {({ placement, isOpen, toggle, close, referenceRef, popperRef }) => (
          <div
            style={{ flexGrow: 1 }}
            onBlur={() => {
              close();
            }}
          >
            <div ref={referenceRef}>
              <ButtonStyled
                onClick={() => {
                  toggle();
                  if (referenceRef.current && referenceRef.current.children.length > 0)
                    referenceRef.current.children[0].focus();
                }}
              >
                <Box mr={{ sm: '300' }}>
                  <Icon icon="ellipsis-h" size={IconSize.MEDIUM} fillColor="blue.800" />
                </Box>
                <Text size={'300'} color={'grey.1000'}>
                  <Trans>More</Trans>
                </Text>
                {photosCount > 0 && <PhotoNotificationStyled>{photosCount}</PhotoNotificationStyled>}
              </ButtonStyled>
            </div>
            <div ref={popperRef} data-placement={placement} style={{ zIndex: 1 }}>
              {isOpen && workListMenuItems && workListMenuItems.length > 0 && (
                <MenuOptions>
                  {workListMenuItems.map((menuItem) => (
                    <MenuItemStyled
                      key={menuItem.type}
                      disabled={menuItem.type === WorkListMenuType.CREATE_BLOCK && !canCreateBlock}
                      onMouseDown={(event) => {
                        onMenuSelect(menuItem.type);
                        close();
                        event.preventDefault();
                      }}
                    >
                      <Text size="400" color="grey.1000">
                        {i18n._(menuItem.label)}
                      </Text>
                      {menuItem.type === WorkListMenuType.ADD_VIEW_PHOTOS && photosCount > 0 && (
                        <Box ml={{ sm: '450' }}>
                          <PhotoNotificationStyled alignItems={{ sm: 'center' }} isMenuItem={true}>
                            {photosCount}
                          </PhotoNotificationStyled>
                        </Box>
                      )}
                    </MenuItemStyled>
                  ))}
                </MenuOptions>
              )}
            </div>
          </div>
        )}
      </Menu>
    </Box>
  );
};
